/**
 * GlobalWebPhoneDock — persistent in-browser softphone for inbound calls.
 *
 * Lifts the WebRTC `<RCWebPhone />` out of page-level Communications /
 * SalesDialer mounts and into the app shell so:
 *   • the SIP registration stays alive while reps navigate the CRM
 *   • inbound calls fire the WebPhone's own "incoming call" UI on EVERY
 *     route — not just Communications
 *   • each rep registers SIP for THEIR OWN extension (per-rep OAuth)
 *
 * Mounting policy:
 *   • Only mounts when the rep has completed per-rep OAuth (status.connected).
 *     If they haven't connected, we render a tiny "Connect RingCentral"
 *     CTA pill in the corner instead — keeps the surface area minimal
 *     until the rep is fully provisioned.
 *   • Skips itself on /communications and /dialer because those pages
 *     embed their own `<RCWebPhone>`. Two SIP registrations from the same
 *     extension would cause RC to kick the older one off, leading to flaky
 *     ring delivery.
 *   • Dock-mode UI: a 56×56 floating pill at bottom-right that expands to
 *     the full softphone on click. Stays collapsed until interaction.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Phone, PhoneOff, X, Move } from "lucide-react";
import { API, useAuth } from "@/App";
import RCWebPhone from "@/components/rc/RCWebPhone";
import { DispositionModal } from "@/pages/SalesDialerLive";
import { toast } from "sonner";
// Iter 346 — coordinated bottom-right lane so the expanded softphone
// panel can never be dropped on top of the shared dock pill.
import {
  FLOATER_MIN_BOTTOM,
  FLOATER_MIN_RIGHT,
  clampFloaterPos,
  defaultFloaterPos,
} from "@/lib/floatingLayout";

const PAGES_WITH_INLINE_WEBPHONE = ["/communications", "/dialer"];

// Iter 158 (Josh) — issue reports #12 & #39: persist dock position
// across mount/unmount so reps don't have to re-drag every page-load.
const DOCK_POS_KEY = "rc.dock.pos";
function loadDockPos() {
  try {
    const raw = sessionStorage.getItem(DOCK_POS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.right === "number" && typeof v?.bottom === "number") {
      // Iter 346 — clamp so a legacy saved position from before the
      // shared lane existed can't leave the expanded panel sitting on
      // top of the dock pill / Live Notes / AI Assist.
      return clampFloaterPos(v);
    }
  } catch { /* ignore */ }
  return null;
}
function saveDockPos(p) {
  try { sessionStorage.setItem(DOCK_POS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

export default function GlobalWebPhoneDock() {
  const { user, token } = useAuth() || {};
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(null); // null | {configured, connected}
  // Exec QA fix (Feb 2026): when a lead-focus page fires
  // `webphone:dial` with `{ number, leadId, leadName }`, auto-expand
  // the dock and pre-fill the dialer with the number so the rep doesn't
  // have to copy-paste from the lead page.
  const [prefillNumber, setPrefillNumber] = useState("");
  // Iter 146 (Danny #4164a796 — May 2026): also capture leadId/leadName
  // so the WebPhone log endpoint receives an explicit lead_id even for
  // leads with no phone on file (where backend auto-match by phone fails).
  const [activeLead, setActiveLead] = useState(null);
  // Iter 158 (Josh) — issue reports #12 & #39: dock is now draggable
  // and remembers position. Also: clicking the X "minimizes" rather than
  // unmounting RCWebPhone so the active call (SIP session + audio)
  // survives the collapse — was the actual root cause of #12 ("if we
  // click the x it disconnects the call").
  const [pos, setPos] = useState(() => loadDockPos() || defaultFloaterPos());
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startRight: 0, startBottom: 0 });
  const onDragStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
    };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragRef.current.startX;
      const dy = clientY - dragRef.current.startY;
      // Dragging right/down means RIGHT/BOTTOM decrease (moves dock toward TL).
      // Iter 346 — enforce shared-lane minimums so the expanded panel
      // cannot cover the collapsed dock pill or Live Notes.
      const next = {
        right: Math.max(FLOATER_MIN_RIGHT, Math.min(window.innerWidth - 80, dragRef.current.startRight - dx)),
        bottom: Math.max(FLOATER_MIN_BOTTOM, Math.min(window.innerHeight - 80, dragRef.current.startBottom - dy)),
      };
      setPos(next);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      saveDockPos(pos);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [pos]);

  // Iter 158 (Josh) #12/#39 — track active call so we can label the
  // collapsed pill "On call · 1m 23s" instead of just a phone icon.
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [callTick, setCallTick] = useState(0);
  useEffect(() => {
    const onStart = () => setCallStartedAt(Date.now());
    const onEnd = () => setCallStartedAt(null);
    window.addEventListener("webphone:call-started", onStart);
    window.addEventListener("webphone:call-ended", onEnd);
    return () => {
      window.removeEventListener("webphone:call-started", onStart);
      window.removeEventListener("webphone:call-ended", onEnd);
    };
  }, []);
  useEffect(() => {
    if (!callStartedAt) return;
    const id = setInterval(() => setCallTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [callStartedAt]);
  const callDurationSec = callStartedAt ? Math.floor((Date.now() - callStartedAt) / 1000) : 0;
  const callDurationLabel = callStartedAt
    ? `${Math.floor(callDurationSec / 60)}m ${callDurationSec % 60}s`
    : null;
  // Reference callTick to silence the unused-var lint (it's the trigger).
  void callTick;
  useEffect(() => {
    const onDial = (ev) => {
      const num = ev?.detail?.number || "";
      if (!num) return;
      setPrefillNumber(num);
      setActiveLead({
        id: ev?.detail?.leadId || null,
        name: ev?.detail?.leadName || null,
        campaignId: ev?.detail?.campaignId || null,
        queueItemId: ev?.detail?.queueItemId || null,
      });
      setExpanded(true);
    };
    // Iter 178.5 (Tatiana #9a1663e4 Feb 2026) — SOFT prefill when the
    // user navigates between leads. Different event from `webphone:dial`
    // because we want to update the number field + active-lead context
    // WITHOUT auto-expanding the dock (the explicit Call action keeps
    // its own behavior). Also no-ops when the rep is already on a call —
    // never disturb the active session.
    const onPrefillLead = (ev) => {
      const num = ev?.detail?.number || "";
      if (!num) return;
      // callStartedAt is the source-of-truth for "rep is on a call".
      if (callStartedAt) return;
      setPrefillNumber(num);
      setActiveLead({
        id: ev?.detail?.leadId || null,
        name: ev?.detail?.leadName || null,
      });
      // Intentionally NOT calling setExpanded(true) here.
    };
    window.addEventListener("webphone:dial", onDial);
    window.addEventListener("webphone:prefill-lead", onPrefillLead);
    return () => {
      window.removeEventListener("webphone:dial", onDial);
      window.removeEventListener("webphone:prefill-lead", onPrefillLead);
    };
  }, [callStartedAt]);

  // Don't mount on pages that already host an inline WebPhone — duplicate
  // SIP registrations from the same extension collide.
  //
  // Iter 192e (Priyanka, June 23 video — "connecting but no sound / no
  // dialpad"): the previous predicate skipped ANY route starting with
  // "/dialer/" — including "/dialer/live". But SalesDialerLive does NOT
  // mount its own RCWebPhone, so suppressing the global dock there meant
  // the `webphone:dial` event fired by the lead-info slide-in had NO
  // listener: no SIP registration, no audio path, no dialpad. The user
  // saw the toast then nothing. Skipping is now EXACT-MATCH only.
  const skipForRoute = useMemo(() => {
    const p = location.pathname || "";
    return PAGES_WITH_INLINE_WEBPHONE.includes(p);
  }, [location.pathname]);

  useEffect(() => {
    if (!token || !user) { setStatus(null); return; }
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/integrations/ringcentral/oauth/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStatus(r.data || { configured: false, connected: false });
      } catch {
        if (!cancelled) setStatus({ configured: false, connected: false });
      }
    };
    fetchStatus();
    // Refresh every 60s so the dock auto-activates within a minute of the
    // rep completing the OAuth flow on Settings.
    const id = setInterval(fetchStatus, 60_000);
    // Also pick up the postMessage from the OAuth popup for instant activation.
    const onMsg = (ev) => {
      if (ev?.data && ev.data.type === "rc-oauth" && ev.data.success) fetchStatus();
    };
    window.addEventListener("message", onMsg);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("message", onMsg);
    };
  }, [token, user]);

  // ── Global after-call disposition (Iter 252) ──────────────────────
  // Auto-open the SHARED DispositionModal when a WebPhone call ends off a
  // page that doesn't host its own handler (Leads +Log, Tasks Call Now,
  // etc.). LeadFocus and the live dialer open it themselves, so they're
  // skipped here to avoid a double modal.
  const [dispLead, setDispLead] = useState(null);
  const [dispTimer, setDispTimer] = useState(0);
  useEffect(() => {
    const onEnded = async (ev) => {
      const p = location.pathname || "";
      if (p.startsWith("/lead-focus") || p === "/dialer/live") return; // local handler owns it
      const leadId = ev?.detail?.lead_id;
      if (!leadId) return;
      // Iter 261 — client calls (lead_id="ehi-<id>") must NEVER open the
      // lead outcome/disposition modal. Outcome logging is leads-only;
      // client calls just auto-log with duration + transcript.
      if (String(leadId).startsWith("ehi-")) return;
      setDispTimer(ev?.detail?.duration_sec || 0);
      try {
        const r = await axios.get(`${API}/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 });
        const l = r.data || {};
        setDispLead({ id: leadId, clinic: l.clinic || l.company || l.name || ev?.detail?.lead_name || "Lead", company: l.company, stage: l.stage || "contacted", subStatus: l.subStatus || l.sub_status || "" });
      } catch {
        setDispLead({ id: leadId, clinic: ev?.detail?.lead_name || "Lead", stage: "contacted", subStatus: "" });
      }
    };
    window.addEventListener("webphone:call-ended", onEnded);
    return () => window.removeEventListener("webphone:call-ended", onEnded);
  }, [location.pathname, token]);

  const afterCallModal = dispLead ? (
    <DispositionModal
      lead={dispLead}
      timer={dispTimer}
      notes={[]}
      contactsCalled={[]}
      onClose={() => setDispLead(null)}
      onSave={async (p) => {
        try {
          await axios.post(`${API}/dispositions`, {
            lead_id: dispLead.id,
            outcome: p.outcome, callType: p.callType, sentiment: p.sentiment,
            stage: p.stage, substatus: p.substatus, nextStep: p.nextStep,
            decisionMakerReached: p.decisionMakerReached, budgetConfirmed: p.budgetConfirmed,
            recordingUrl: p.recordingUrl, notes: p.notes, followup: p.followup,
            advance: p.advance, completed: p.completed !== false,
            contactDispositions: p.contactDispositions, leadEdits: p.leadEdits,
            aiApplied: p.aiApplied, attachments: p.attachments, followupTasks: p.followupTasks,
            productTags: p.productTags, duration_seconds: dispTimer,
          }, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
          toast.success("Disposition saved");
          setDispLead(null);
          try { window.dispatchEvent(new CustomEvent("disposition:completed")); } catch { /* best-effort */ }
          return true;
        } catch (err) {
          const st = err?.response?.status;
          const d = err?.response?.data?.detail;
          toast.error(`Save failed (${st || "network"})${typeof d === "string" ? ` · ${d}` : ""} — please retry.`, { duration: 10000 });
          return false;
        }
      }}
    />
  ) : null;

  if (!user || skipForRoute || !status?.configured) return afterCallModal;

  // Disconnected state — tiny CTA pill (clicking it opens Settings).
  if (!status.connected) {
    return (
      <>
      {afterCallModal}
      <a
        href="/settings?section=integrations"
        data-testid="global-rc-connect-cta"
        style={{
          // No position:fixed — hosted inside the bottom-dock container in
          // MainLayout next to AIChatFab so they can never overlap.
          height: 36, boxSizing: "border-box",
          background: "rgba(255,255,255,0.85)", border: "0.5px solid #fcd34d", color: "#92400e",
          borderRadius: 999, padding: "0 12px", fontSize: 11, fontWeight: 600,
          textDecoration: "none", display: "inline-flex", alignItems: "center",
          gap: 6, flexShrink: 0,
        }}
        title="Connect your RingCentral to enable the in-browser softphone"
      >
        <Phone style={{ width: 13, height: 13 }} />
        Connect RingCentral
      </a>
      </>
    );
  }

  // Iter 158 (Josh) #12/#39 — render BOTH pill and panel, toggle
  // visibility via CSS, so RCWebPhone stays mounted and the SIP
  // session (+ active audio) survives a collapse. This is the actual
  // fix for "if we click the x it disconnects the call".
  // The pill is the bottom-dock entry point (when collapsed AND there's
  // no active call drag-position to preserve). The panel slides in at
  // the persisted pos when expanded.
  //
  // Iter 170.7 (Ioannis 2026-06-05 — "clicked the x and the call ended"):
  // the previous implementation had TWO conditional returns (one for
  // expanded, one for collapsed), each containing its OWN <RCWebPhone />.
  // React reconciles by tree shape — when the whole tree shape flips
  // on toggle, RCWebPhone unmounts and remounts, killing the SIP
  // session. The fix is to render ONE stable wrapper that contains a
  // SINGLE RCWebPhone mount, and toggle pill-vs-panel chrome via CSS
  // visibility instead of conditional JSX.
  const onCall = !!callStartedAt;
  return (
    <>
      {afterCallModal}
      {/* Collapsed pill — hidden via CSS when expanded so RCWebPhone
          inside the dock keeps the same React tree position.
          Iter 346.1 — no `position: fixed` here. GlobalWebPhoneDock is
          mounted INSIDE the shared bottom-dock in App.js, so the pill
          just flows next to AIChatFab / NotificationBell / Live Notes.
          The previous fixed anchor at right:16 bottom:16 physically
          stacked this pill on top of the NotificationBell. */}
      {!expanded && (
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
          }}
        >
          <button
            onClick={async () => {
              const m = (location.pathname || "").match(/^\/lead-focus\/([^/?]+)/);
              if (m && token) {
                try {
                  const r = await axios.get(`${API}/leads/${m[1]}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const ld = r.data || {};
                  const ph = (ld.contacts?.[0]?.phone) || ld.phone || "";
                  if (ph) setPrefillNumber(ph);
                  setActiveLead({
                    id: ld.id || m[1],
                    name: ld.contact_name || ld.name || ld.company || null,
                  });
                } catch { /* non-fatal */ }
              }
              setExpanded(true);
            }}
            data-testid="global-webphone-pill"
            aria-label={onCall ? "Open softphone (on call)" : "Open softphone"}
            style={{
              height: 36, boxSizing: "border-box",
              background: onCall
                ? "linear-gradient(135deg,#dc2626 0%,#991b1b 100%)"
                : "linear-gradient(135deg,#10b981 0%,#059669 100%)",
              color: "#fff", border: 0, cursor: "pointer",
              boxShadow: onCall ? "0 2px 6px rgba(220,38,38,0.35)" : "0 2px 6px rgba(16,185,129,0.25)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              gap: 6, flexShrink: 0,
              padding: onCall ? "0 12px" : 0,
              width: onCall ? "auto" : 36,
              borderRadius: onCall ? 18 : "50%",
              fontSize: 11, fontWeight: 600,
              animation: onCall ? "rc-pulse 1.6s ease-in-out infinite" : "none",
            }}
          >
            <Phone style={{ width: 14, height: 14 }} />
            {onCall && <span data-testid="global-webphone-pill-duration">On call · {callDurationLabel}</span>}
          </button>
          {/* Iter 192i (June 24 agent feedback — "Called back a lead and clicked
              the icon to end the call in the bottom right. The call did not end.
              He had to click the other dial pad in the middle of the screen
              to end the call"). The pill on the right used to ONLY open the
              softphone — there was no quick hang-up. Added a dedicated red
              hang-up button next to the pill when on a call so reps can
              terminate from anywhere in the app with one click. Dispatches
              `webphone:control` so the inline RCWebPhone (whichever instance
              is currently registered) tears the session down. */}
          {onCall && (
            <button
              type="button"
              onClick={() => {
                try {
                  window.dispatchEvent(new CustomEvent("webphone:control", { detail: { action: "hangup" } }));
                } catch { /* defensive */ }
              }}
              data-testid="global-webphone-pill-hangup"
              aria-label="End call"
              title="End call"
              style={{
                width: 36, height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#7f1d1d 0%,#450a0a 100%)",
                border: 0, cursor: "pointer", color: "#fff",
                boxShadow: "0 2px 6px rgba(127,29,29,0.45)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PhoneOff style={{ width: 16, height: 16 }} />
            </button>
          )}
        </div>
      )}

      <div
        data-testid="global-webphone-dock"
        data-expanded={expanded ? "true" : "false"}
        style={{
          position: "fixed", right: pos.right, bottom: pos.bottom, zIndex: 9000,
          width: 360, maxHeight: "85vh", overflowY: "auto",
          background: "#fff", borderRadius: 14,
          boxShadow: "0 10px 32px rgba(0,0,0,0.18)",
          border: "0.5px solid var(--border, #e2e8f0)",
          userSelect: dragRef.current.active ? "none" : "auto",
          // Hide the panel when collapsed — keep RCWebPhone mounted.
          visibility: expanded ? "visible" : "hidden",
          opacity: expanded ? 1 : 0,
          pointerEvents: expanded ? "auto" : "none",
          transition: "opacity 120ms",
        }}
      >
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          data-testid="global-webphone-drag-handle"
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", borderBottom: "0.5px solid var(--border)",
            background: "#f8fafc", borderTopLeftRadius: 14, borderTopRightRadius: 14,
            cursor: "move",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#334155" }}>
            <Move style={{ width: 12, height: 12, color: "#94a3b8" }} />
            <Phone style={{ width: 13, height: 13, color: callStartedAt ? "#dc2626" : "#10b981" }} />
            Softphone
            {callStartedAt && (
              <span data-testid="global-webphone-call-duration" style={{ marginLeft: 6, fontSize: 10, color: "#dc2626", background: "#fee2e2", padding: "1px 7px", borderRadius: 8, fontWeight: 700 }}>
                On call · {callDurationLabel}
              </span>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            data-testid="global-webphone-collapse"
            aria-label="Minimize softphone (call stays active)"
            title="Minimize — your call stays active"
            style={{ background: "none", border: 0, cursor: "pointer", padding: 4, color: "#64748b" }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
        <div style={{ padding: 6 }}>
          <RCWebPhone prefillNumber={prefillNumber} activeLead={activeLead} />
        </div>
      </div>

      <style>{`
        @keyframes rc-pulse {
          0%, 100% { box-shadow: 0 2px 6px rgba(220,38,38,0.35); }
          50%      { box-shadow: 0 2px 14px rgba(220,38,38,0.65); }
        }
      `}</style>
    </>
  );
}
